import React, { useCallback, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CloudUpload from '@mui/icons-material/CloudUpload';
import AttachFile from '@mui/icons-material/AttachFile';
import { ALLOWED_EXTENSIONS, MAX_FILES_PER_UPLOAD, MAX_FILE_SIZE, QuotaStatus } from '../../types/Upload';

interface FileDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  quotaStatus?: QuotaStatus | null;
  /** The agency accepts CAD event report PDFs with the audio (CadBulkUploadEnabled). */
  acceptCadPdf?: boolean;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFilesSelected,
  disabled = false,
  quotaStatus,
  acceptCadPdf = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFilesSelected(files);
    }
  }, [onFilesSelected, disabled]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFilesSelected(files);
    }
    // Reset input value to allow selecting the same files again
    e.target.value = '';
  }, [onFilesSelected, disabled]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = (acceptCadPdf ? [...ALLOWED_EXTENSIONS, '.pdf'] : ALLOWED_EXTENSIONS).join(',');
    input.onchange = (e) => {
      if (e.target && (e.target as HTMLInputElement).files) {
        handleFileSelect(e as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    };
    input.click();
  }, [handleFileSelect, disabled, acceptCadPdf]);

  return (
    <Paper
      elevation={isDragOver ? 4 : 1}
      sx={{
        p: 4,
        textAlign: 'center',
        border: isDragOver ? '2px dashed' : '2px dashed transparent',
        borderColor: isDragOver ? 'primary.main' : 'grey.300',
        backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: disabled ? 'background.paper' : 'action.hover',
          borderColor: disabled ? 'grey.300' : 'primary.main',
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <CloudUpload 
        sx={{ 
          fontSize: 48, 
          color: isDragOver ? 'primary.main' : 'text.secondary',
          mb: 2 
        }} 
      />
      
      <Typography variant="h6" gutterBottom>
        {isDragOver
          ? 'Drop files here'
          : acceptCadPdf
            ? 'Drag and drop audio files and CAD reports here'
            : 'Drag and drop audio files here'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        or
      </Typography>
      
      <Button
        variant="outlined"
        startIcon={<AttachFile />}
        disabled={disabled}
        sx={{ mb: 2 }}
      >
        Choose Files
      </Button>
      
      <Typography variant="caption" display="block" color="text.secondary">
        Supported formats: {ALLOWED_EXTENSIONS.join(', ')}
      </Typography>
      {acceptCadPdf && (
        <Typography variant="caption" display="block" color="text.secondary">
          CAD event reports (.pdf) are matched to their calls by incident number
        </Typography>
      )}
      <Typography variant="caption" display="block" color="text.secondary">
        Maximum {quotaStatus ? Math.min(MAX_FILES_PER_UPLOAD, quotaStatus.limit) : MAX_FILES_PER_UPLOAD} files per upload
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        Maximum {quotaStatus?.maxAudioDurationMinutes || 45} minutes per file
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        Maximum {MAX_FILE_SIZE / (1024 * 1024)}MB per file
      </Typography>

    </Paper>
  );
};